import { StatusIndicator } from '@/components/common/StatusIndicator';
import SVGImage from '@/components/layout/SVGImage';
import { addDays, endOfMonth, format, startOfMonth } from 'date-fns';
import {
  formatInTimeZone,
  format as formatTZ,
  fromZonedTime,
} from 'date-fns-tz';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container as MapDiv, Marker, NaverMap } from 'react-naver-maps';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { OutlinedButton } from '../components/common/OutlinedButton';
import DateSelector from '../components/forecast/DateSelector';
import Dropdown from '../components/forecast/Dropdown';
import SearchButton from '../components/forecast/SearchButton';
import PlantOverviewSummaryCard from '../components/plant-overview/PlantOverviewSummaryCard';
import {
  PowerOutageScheduleInsertModal,
  setTimes,
} from '../components/plant-overview/PowerOutageScheduleInsertModal';
import { PowerOutageScheduleLimitPagination } from '../components/plant-overview/PowerOutageScheduleLimitPagination';
import PowerOutageScheduleTable, {
  PowerOutageScheduleData,
} from '../components/plant-overview/PowerOutageScheduleTable';
import { useAppDispatch } from '../hooks/useRedux';
import SideLayout from '../layouts/SideLayout';
import {
  fetchCustomerData,
  fetchMeterDailyData,
  fetchMeterMonthlyData,
  fetchSiteName,
  resetCustomerState,
  resetMeterDailyState,
  resetMeterMonthlyState,
  resetSiteNameState,
} from '../slices/overviewSlice';
import { RootState } from '../store';
import { del, get } from '../utils/fetch';
import ErrorBoundary from './ErrorBoundary';

const PlantOverviewPage = () => {
  const tablePresentationUnit = 10;
  const [currentIndex, setCurrentIndex] = useState<number | null>(0);
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [navigating, setNavigating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedSiteIdIndex, setSelectedSiteIdIndex] = useState(0);
  const [modalStartDate, setModalStartDate] = useState(
    setTimes(new Date(), 12)
  );
  const [modalEndDate, setModalEndDate] = useState(
    setTimes(addDays(new Date(), 1), 12)
  );
  const [limitDataInitialValue, setLimitDataInitialValue] = useState<
    Array<PowerOutageScheduleLimitData>
  >([]);
  const [sites, setSites] = useState<
    Array<{ siteId: string; siteName: string }>
  >([]);
  const navigate = useNavigate();

  const dateOnChange = useCallback((date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  }, []);

  const {
    sites: {
      data: siteNameData,
      loading: siteNameLoading,
      error: siteNameError,
    },
    customer: {
      data: overviewCustomerData,
      loading: overviewCustomerLoading,
      error: overviewCustomerError,
    },
    meterDaily: {
      data: meterDailyData,
      loading: meterDailyLoading,
      error: meterDailyError,
    },
    meterMonthly: {
      data: meterMonthlyData,
      loading: meterMonthlyLoading,
      error: meterMonthlyError,
    },
  } = useSelector((state: RootState) => state);
  const dispatch = useAppDispatch();

  const isNaverMapPositionValid = useMemo(() => {
    return (
      overviewCustomerData?.status === 'success' &&
      overviewCustomerData.data.latitude &&
      overviewCustomerData.data.longitude
    );
  }, [overviewCustomerData]);

  const naverMapDefaultPosition = useMemo(() => {
    const position = isNaverMapPositionValid
      ? {
          lat: parseFloat(overviewCustomerData!.data.latitude!),
          lng: parseFloat(overviewCustomerData!.data.longitude!),
        }
      : { lat: 37.5666103, lng: 126.9783882 };

    return position;
  }, [overviewCustomerData]);

  const debouncedFetchCustomerData = useCallback(
    debounce((params) => {
      dispatch(fetchCustomerData(params));
    }, 1000),
    [dispatch]
  );

  const debouncedFetchMeterDailyData = useCallback(
    debounce((params) => {
      dispatch(fetchMeterDailyData(params));
    }, 1000),
    [dispatch]
  );

  const debouncedFetchMeterMonthlyData = useCallback(
    debounce((params) => {
      dispatch(fetchMeterMonthlyData(params));
    }, 1000),
    [dispatch]
  );

  const debouncedFetchSiteName = useCallback(
    debounce((params) => dispatch(fetchSiteName(params)), 1000),
    [get]
  );

  const sitesByName = useMemo(
    () => sites.map((site) => site.siteName),
    [sites]
  );

  const selectedSite = useMemo(
    () => (sites.length > 0 ? sites[selectedSiteIdIndex] : null),
    [sites, selectedSiteIdIndex]
  );

  const handleSiteSelected = useCallback(
    (name: string) => {
      const selectedSiteIndex = sites?.findIndex(
        (site: { siteId: string; siteName: string }) => site.siteName === name
      );
      if (selectedSiteIndex >= 0) {
        setSelectedSiteIdIndex(selectedSiteIndex);
      }
    },
    [sites, setSelectedSiteIdIndex]
  );

  const debouncedDeleteStopSchedule = useCallback(
    debounce((startTime: string, endTime: string) => {
      if (selectedSite) {
        del('/customer/site/stop-schedule', {
          params: {
            site: selectedSite.siteId,
            startTime,
            endTime,
          },
        }).then((response) => {
          if (response.status === 'success')
            debouncedFetchStopSchedule(selectedSite.siteId);
        });
      }
    }, 1000),
    [del, selectedSite]
  );

  const handleModalOnClick = () => {
    if (selectedSite) debouncedFetchStopSchedule(selectedSite.siteId);
    setShowModal(false);
  };

  const handleEditStopSchedule = (
    startTime: string,
    endTime: string,
    utilRateGenerators: Array<{ number: number; value: number }>
  ) => {
    const startDate = fromZonedTime(startTime, 'Asia/Seoul');
    const endDate = fromZonedTime(endTime, 'Asia/Seoul');
    setModalStartDate(startDate);
    setModalEndDate(endDate);
    const limitData = utilRateGenerators.map((generator, i) => ({
      name: generator.number.toString(),
      value: generator.value.toString(),
      index: i,
      isLock: false,
    }));
    setLimitDataInitialValue(limitData);
    setIsUpdate(true);
    setShowModal(true);
  };

  const initScheduleProps = () => {
    setModalStartDate(setTimes(new Date(), 12));
    setModalEndDate(setTimes(addDays(new Date(), 1), 12));
    const genNum = parseInt(overviewCustomerData?.data.genNum ?? '0');
    setLimitDataInitialValue(
      genNum > 0
        ? Array.from({ length: genNum }, (_, i) => ({
            name: (i + 1).toString(),
            value: '100',
            index: i,
            isLock: false,
          }))
        : []
    );
    setIsUpdate(false);
  };

  const handleAddScheduleButton = () => {
    initScheduleProps();
    setShowModal(true);
  };

  const [stopSchedules, setStopSchedules] = useState<
    Array<PowerOutageScheduleData>
  >([]);

  const getFullDateRangeThisMonth = useCallback((): {
    startTime: string;
    endTime: string;
  } => {
    const timeZone = 'Asia/Seoul'; // for +09:00

    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);

    const startTime = formatTZ(start, "yyyy-MM-dd'T'00:00:00XXX", { timeZone });
    const endTime = formatTZ(end, "yyyy-MM-dd'T'23:59:59XXX", { timeZone });

    return { startTime, endTime };
  }, [startOfMonth, endOfMonth, formatTZ, selectedDate]);

  const debouncedFetchStopSchedule = useCallback(
    debounce(
      (siteId: string) =>
        get<{ data: Array<SiteStopScheduleEntity<Record<string, never>>> }>(
          '/customer/site/stop-schedule',
          {
            params: {
              site: siteId,
              ...getFullDateRangeThisMonth(),
            },
          }
        ).then((response) => {
          if (response.status === 'success') {
            const stopSchedules = response.data;
            const formattedStopSchedules = stopSchedules.map((stopSchedule) => {
              const {
                startTime,
                endTime,
                memo,
                capacityMwh,
                lastModifiedTime,
                utilRate_site,
                active,
                utilRate_generators,
              } = stopSchedule;
              const formattedStartTime = formatInTimeZone(
                startTime,
                'Asia/Seoul',
                'yyyy.MM.dd HH:mm'
              );
              const formattedEndTime = formatInTimeZone(
                endTime,
                'Asia/Seoul',
                'yyyy.MM.dd HH:mm'
              );
              const target = utilRate_generators.reduce(
                (prev, cur) => {
                  if (cur.value === 100) return prev;
                  if (prev.initial === '') {
                    prev.initial = cur.number.toString();
                  }
                  prev.count++;

                  return prev;
                },
                { initial: '', count: 0 }
              );

              return {
                date: `${formattedStartTime} - ${formattedEndTime}`,
                capacity: capacityMwh,
                utilization: utilRate_site,
                target:
                  target.count === 0
                    ? ''
                    : target.count === 1
                      ? target.initial
                      : `${target.initial}${t('overview.modal.limit.generator')} 외 ${target.count.toString()}개`,
                lastUpdated: lastModifiedTime,
                memo,
                active,
                startTime,
                endTime,
                utilRateGenerators: utilRate_generators,
              } as unknown as PowerOutageScheduleData;
            });
            setStopSchedules(formattedStopSchedules);
          }
        }),
      1000
    ),
    [setStopSchedules, get, getFullDateRangeThisMonth]
  );

  useEffect(() => {
    debouncedFetchSiteName({
      endpoint: '/customer/site/name',
      fetch: get,
    });
  }, []);

  useEffect(() => {
    if (!siteNameLoading && siteNameData) {
      setSites(
        siteNameData.data.filter((v) => v.siteId && v.siteName) as Array<{
          siteId: string;
          siteName: string;
        }>
      );
    }
  }, [siteNameLoading, siteNameData]);

  useEffect(() => {
    if (selectedSite) {
      debouncedFetchCustomerData({
        endpoint: '/customer/site',
        fetch: get,
        options: {
          params: {
            siteId: selectedSite.siteId,
          },
        },
      });
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite && selectedDate) {
      debouncedFetchMeterDailyData({
        endpoint: '/meter/daily',
        fetch: get,
        options: {
          params: {
            site: selectedSite.siteId,
            ymd: format(selectedDate, 'yyyy-MM-dd'),
          },
        },
      });
      debouncedFetchMeterMonthlyData({
        endpoint: '/meter/monthly',
        fetch: get,
        options: {
          params: {
            site: selectedSite.siteId,
            ym: format(selectedDate, 'yyyy-MM'),
          },
        },
      });

      debouncedFetchStopSchedule(selectedSite.siteId);
    }
  }, [selectedDate, selectedSite]);

  useEffect(() => {
    if (
      !siteNameLoading &&
      !overviewCustomerLoading &&
      !meterDailyLoading &&
      !meterMonthlyLoading &&
      (siteNameError?.code === 'A0001' ||
        overviewCustomerError?.code === 'A0001' ||
        meterDailyError?.code === 'A0001' ||
        meterMonthlyError?.code === 'A0001')
    ) {
      if (!navigating) {
        setNavigating(true);
        dispatch(resetCustomerState());
        dispatch(resetMeterDailyState());
        dispatch(resetMeterMonthlyState());
        dispatch(resetSiteNameState());
        navigate('/');
        setNavigating(false);
      }
    }
  }, [
    siteNameError,
    siteNameLoading,
    overviewCustomerLoading,
    overviewCustomerError,
    meterDailyError,
    meterDailyLoading,
    meterMonthlyError,
    meterMonthlyLoading,
  ]);

  // Submit Related Methods
  const onClickSearchButton = () => {
    if (selectedSite) {
      debouncedFetchCustomerData({
        endpoint: '/customer/site',
        fetch: get,
        options: {
          params: {
            siteId: selectedSite.siteId,
          },
        },
      });
      debouncedFetchMeterDailyData({
        endpoint: '/meter/daily',
        fetch: get,
        options: {
          params: {
            site: selectedSite.siteId,
            ymd: format(selectedDate, 'yyyy-MM-dd'),
          },
        },
      });
      debouncedFetchMeterMonthlyData({
        endpoint: '/meter/monthly',
        fetch: get,
        options: {
          params: {
            site: selectedSite.siteId,
            ym: format(selectedDate, 'yyyy-MM'),
          },
        },
      });
      debouncedFetchStopSchedule(selectedSite.siteId);
    }
  };

  return (
    <SideLayout>
      <div className="flex min-h-screen min-w-[1200px] flex-col whitespace-nowrap bg-light-2 px-[50px] py-[50px] [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-[1100px]">
        <header className="flex">
          {siteNameLoading ? (
            <StatusIndicator />
          ) : siteNameData === null && siteNameError ? (
            <StatusIndicator isError={true} />
          ) : (
            <>
              <Dropdown
                items={sitesByName}
                testId="overview-sites-dropdown"
                selectedItem={sitesByName[selectedSiteIdIndex]}
                className="min-w-[240px] text-dark-3 [&>div:first-child>button]:noto-medium-4 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[40px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:pb-[13px] [&>div:first-child>button]:pl-[21px] [&>div:first-child>button]:pr-[15px] [&>div:first-child>button]:pt-[15px] [&>div:first-child>button]:ring-2 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&[data-headlessui-state=open]>div:first-child>button]:bg-light-2 [&[data-headlessui-state=open]>div:first-child>button]:ring-teal-dark"
                onSelected={handleSiteSelected}
              ></Dropdown>
              <DateSelector
                testId={'overview-test'}
                date={selectedDate}
                onChange={dateOnChange}
                className="ml-5 [&>div>div>input]:h-[40px] [&>div>div>input]:border-[1px] [&>div>div>input]:border-[#CDD5D5] [&>div>div>input]:bg-white"
                inputBase="border-[1px] border-dark-8 bg-white"
              ></DateSelector>
              <SearchButton
                onClick={onClickSearchButton}
                text={t('overview.search')}
                className="ml-[10px] h-[40px] w-[80px] cursor-pointer"
              ></SearchButton>
            </>
          )}
        </header>
        <section>
          <p className="pop-semibold-5 mt-[30px] leading-none text-[#1F384C]">
            {sitesByName[selectedSiteIdIndex]
              ? t('overview.header', {
                  value: sitesByName[selectedSiteIdIndex],
                })
              : '-'}
          </p>
          {/* Map */}
          <ErrorBoundary
            fallback={
              <div className="z-1 absolute top-0 h-[360px] w-[100%] rounded-[10px] bg-black shadow-3">
                <SVGImage name="naver-map-cover" />
              </div>
            }
          >
            <div className="relative mt-[25px] h-[360px]">
              <MapDiv className="absolute top-0 h-[360px] rounded-[10px] shadow-3 [&>div]:rounded-[10px]">
                <NaverMap center={naverMapDefaultPosition} mapTypeId={'hybrid'}>
                  <Marker position={naverMapDefaultPosition} />
                </NaverMap>
              </MapDiv>
              {!isNaverMapPositionValid && (
                <div className="z-1 absolute top-0 h-[360px] w-[100%] rounded-[10px] bg-dark-2 shadow-3">
                  <SVGImage
                    className="[&]:rounded-[10px]"
                    height={360}
                    width={1100}
                    name="naver-map-cover"
                  />
                </div>
              )}
            </div>
          </ErrorBoundary>
          <div className="mt-[30px] flex justify-between">
            <PlantOverviewSummaryCard
              title={t('overview.capacity')}
              firstItem={t('overview.windEnergy')}
              firstItemValue={`${overviewCustomerData?.data.capacityGenMw ? parseFloat(overviewCustomerData.data.capacityGenMw).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0'} `}
              firstItemUnit={t('overview.schedule.capacity', {
                context: 'unit',
              })}
              secondItem={t('overview.ess')}
              secondItemValue={`${overviewCustomerData?.data.capacityEssMw ? parseFloat(overviewCustomerData.data.capacityEssMw).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0'} `}
              secondItemUnit={t('overview.schedule.capacity', {
                context: 'unit',
              })}
            />
            <PlantOverviewSummaryCard
              title={t('overview.quantity')}
              firstItem={t('overview.acc', { context: 'today' })}
              firstItemValue={`${meterDailyData?.data.powerMeteredMwh ? parseFloat(meterDailyData.data.powerMeteredMwh).toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 }) : '0'} `}
              firstItemUnit={t('forecast.generationPerformance_unit', {
                context: '1',
              })}
              secondItem={t('overview.acc', { context: 'month' })}
              secondItemValue={`${
                meterMonthlyData?.data.powerMeteredMwh
                  ? parseFloat(
                      meterMonthlyData.data.powerMeteredMwh
                    ).toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                      minimumFractionDigits: 1,
                    })
                  : '0'
              } `}
              secondItemUnit={t('forecast.generationPerformance_unit', {
                context: '1',
              })}
            />
            <PlantOverviewSummaryCard
              title={t('overview.utilization')}
              firstItem={t('overview.average', { context: 'today' })}
              firstItemValue={`${meterDailyData?.data.availMetered ? parseFloat(meterDailyData.data.availMetered).toFixed(1) : '0'} `}
              firstItemUnit={t('forecast.percentageError', { context: 'unit' })}
              secondItem={t('overview.average', { context: 'month' })}
              secondItemValue={`${meterMonthlyData?.data.availMetered ? parseFloat(meterMonthlyData.data.availMetered).toFixed(1) : '0'} `}
              secondItemUnit={t('forecast.percentageError', {
                context: 'unit',
              })}
            />
          </div>
        </section>
        {/* 발전 정지 일정 */}
        <section className="mt-[30px] max-h-[600px] rounded-[10px] bg-white px-[30px] py-[20px] shadow-3">
          <div className="flex items-center justify-between">
            <p className="noto-15-medium leading-none">
              {`${t('overview.schedule.title')}(${(selectedDate.getMonth() + 1).toString()}월)`}
            </p>
            <OutlinedButton
              isClicked={showModal}
              testId="add-power-outage-schedule"
              text={t('overview.addButton')}
              onClick={handleAddScheduleButton}
            />
          </div>
          <PowerOutageScheduleTable
            testId="power-outage-schedule-table"
            data={
              currentIndex === null
                ? []
                : stopSchedules.slice(
                    currentIndex * tablePresentationUnit,
                    currentIndex * tablePresentationUnit +
                      tablePresentationUnit >
                      stopSchedules.length
                      ? stopSchedules.length
                      : currentIndex * tablePresentationUnit +
                          tablePresentationUnit
                  )
            }
            className="mt-[20px]"
            handleDelete={debouncedDeleteStopSchedule}
            handleUpdate={handleEditStopSchedule}
          ></PowerOutageScheduleTable>
          <PowerOutageScheduleLimitPagination
            className="mt-4"
            currentIndex={currentIndex}
            setIndex={setCurrentIndex}
            length={Math.ceil(stopSchedules.length / 10)}
            testId="gridus-pagination"
          />
        </section>
      </div>
      {/* 발전 정지 일정 추가 모달 */}
      {showModal && (
        <section
          data-testid="add-power-outage-schedule-modal-background"
          className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black bg-opacity-80"
        >
          <PowerOutageScheduleInsertModal
            siteId={selectedSite?.siteId}
            limitDataInitialValue={limitDataInitialValue}
            capacityGenMw={overviewCustomerData?.data.capacityGenMw}
            onClick={handleModalOnClick}
            className="w-[682px]"
            testId="add-power-outage-schedule-modal"
            startDate={modalStartDate}
            endDate={modalEndDate}
            isUpdate={isUpdate}
          ></PowerOutageScheduleInsertModal>
        </section>
      )}
    </SideLayout>
  );
};

export default PlantOverviewPage;
